import { useSessionCheckin } from "@/components/nds/windows/session/SessionLoan";
import { useAuth } from "@/lib/auth";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { finApi, money } from "@/lib/financeApi";
import { PaycheckRow } from "@/components/nds/windows/finance/PaychecksTab";
import { ArchiveViewChips } from "@/components/nds/ArchiveView";
import { BulkArchiveBar } from "@/components/nds/BulkArchiveBar";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import ReconcilePanel from "@/components/nds/windows/finance/ReconcilePanel";
import { OldPortalToggle } from "@/components/nds/windows/finance/OldPortalToggle";
import { ComingUp } from "@/components/nds/windows/finance/ExpectedPaychecks";
import { DuesResident } from "@/components/nds/windows/finance/flow/Dues";
import { reApi } from "@/lib/residentApi";
import { BulkBar, RowTools } from "@/components/nds/windows/resident/RowTools";
import FinancialReviewToggle from "@/components/nds/windows/resident/FinancialReviewToggle";
import StaffLogPaycheck from "@/components/nds/windows/session/StaffLogPaycheck";
import MoveMoney from "@/components/nds/windows/resident/MoveMoney";
import { CardTile } from "@/components/nds/windows/resident/ResidentCards";
import ArrangeBoard from "@/components/nds/ArrangeBoard";
import { LedgerFilters, EMPTY_LEDGER_FILTERS } from "@/components/nds/windows/finance/LedgerFilters";
import { ArrowLeft, Banknote, ArrowLeftRight, CalendarRange, Receipt, PiggyBank, ShoppingBag, BookText, Landmark, History, Wallet, Home, Briefcase } from "lucide-react";

const Tools = ({ on, kind, row, onChanged, children, sel }) => (on
  ? <RowTools kind={kind} row={row} onChanged={onChanged} checked={sel?.[kind]?.includes(row.id)} onCheck={sel?.toggle(kind)}>{children}</RowTools> : children);

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const words = (s) => (s ? String(s).replace(/_/g, " ") : "—");
const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted mb-2";
const BUCKETS = [["checking", "Cash in Hand"], ["savings", "Savings"], ["loan", "Line of Credit · total"], ["escrow", "Rent"], ["program_fee", "PACT Fee"]];

const TILE_ICON = { checking: Wallet, savings: PiggyBank, loan: Landmark, escrow: Home, program_fee: Briefcase };

/** Balance tile in the Real-time KPI pulse style. */
const BalanceTile = ({ k, label, v }) => {
  const Icon = TILE_ICON[k];
  const owe = k === "loan" ? -(v || 0) : v || 0;
  return (
    <div data-testid={`financial-balance-${k}`} className="nds-card p-5 relative">
      <div className="flex items-start justify-between gap-2"><div className="nds-label">{label}</div>
        <div className="w-9 h-9 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm shrink-0"><Icon className="w-4 h-4" strokeWidth={1.8} /></div></div>
      <div data-testid={`financial-balance-${k}-value`} className={`mt-4 text-2xl md:text-3xl nds-num tabular-nums ${k !== "loan" && owe < 0 ? "!text-amber-200" : ""}`}>{money(Math.abs(owe))}</div>
      <div className="mt-2 font-mono text-[0.65rem] text-nex-muted tracking-widest uppercase">{k === "loan" ? "owed" : owe < 0 ? "due this week" : "balance"}</div>
    </div>
  );
};

const Section = ({ title, testId, children }) => (
  <div data-testid={testId} className="space-y-2"><div className={labelCls}>{title}</div>{children}</div>
);
const Empty = ({ children }) => <p className="text-sm text-nex-muted">{children}</p>;
const Line = ({ testId, main, sub, right, tag }) => (
  <div data-testid={testId} className="flex items-start gap-2 border-b border-white/5 py-1">
    <div className="flex-1"><div className="text-sm text-nex-silver">{main}</div>{sub && <div className="text-xs text-nex-muted">{sub}</div>}</div>
    {tag}
    {right && <div className={`font-mono text-sm text-nex-gold ${tag ? "w-24 text-right shrink-0" : ""}`}>{right}</div>}
  </div>
);
const SRC = [["all", "All"], ["old", "Old portal"], ["new", "New portal"]];
const OriginTag = ({ id, origin }) => (
  <span data-testid={`financial-ledger-origin-${id}`} className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded-sm border font-mono text-[9px] uppercase tracking-[0.14em] ${origin === "old" ? "border-amber-400/50 text-amber-200 bg-amber-500/10" : "border-sky-400/40 text-sky-200 bg-sky-500/10"}`}>
    {origin === "old" ? "Old portal" : "New portal"}</span>
);
const SourceChips = ({ src, setSrc, counts }) => (
  <div data-testid="financial-ledger-source-filter" className="flex flex-wrap gap-1.5 py-1">
    {SRC.map(([k, l]) => (
      <button key={k} type="button" onClick={() => setSrc(k)} data-testid={`financial-ledger-source-${k}`}
        className={`px-2 py-0.5 border rounded-sm font-mono text-[10px] uppercase tracking-[0.14em] transition-colors ${src === k ? "border-nex-gold text-nex-gold" : "border-white/15 text-nex-muted hover:text-nex-silver"}`}>
        {l}{counts && <span className="ml-1 opacity-70">{k === "all" ? counts.old + counts.new : counts[k]}</span>}</button>))}
  </div>
);

const Collapsible = ({ label, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button data-testid={`${testId}-toggle`} onClick={() => setOpen((o) => !o)} className="text-sm text-nex-silver hover:text-white transition-colors">
        {label} · {open ? "Hide ▲" : "Show ▼"}
      </button>
      {open && <div data-testid={`${testId}-list`} className="mt-2">{children}</div>}
    </div>
  );
};


/** Resident paychecks: Active / Archived / All, edit in place, group archive / unarchive, SA permanent delete. */
function PaycheckList({ resident, onMoney, hideId }) {
  const [view, setView] = useState("active");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState([]);
  const [arch, setArch] = useState(null);
  const isSuper = useIsSuper();
  const load = () => finApi.get("/paychecks", { resident_id: resident.id, view }).then((p) => { setRows(p.paychecks); setSel([]); }).catch(() => setRows([]));
  useEffect(() => { load(); }, [resident.id, view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reApi.get("/residents/archive/reasons").then(setArch).catch(() => {}); }, []);
  const changed = () => { load(); onMoney(); window.dispatchEvent(new CustomEvent("fin:paycheck-logged")); };
  const can = arch?.can_archive;
  const list = (rows || []).filter((p) => p.id !== hideId);
  const allOn = list.length > 0 && list.every((p) => sel.includes(p.id));
  return (
    <Section title={`Paychecks · ${list.length}`} testId="financial-paychecks">
      <div className="flex flex-wrap items-center gap-3">
        <ArchiveViewChips prefix="financial-paychecks" view={view} setView={setView} size="sm" />
        {can && list.length > 0 && (
          <label className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.18em] uppercase text-nex-muted">
            <input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : list.map((p) => p.id))} data-testid="financial-paychecks-select-all" /> Select all
          </label>
        )}
      </div>
      {can && <BulkArchiveBar kind="paychecks" label="paychecks" selected={sel} selectedArchived={list.filter((p) => p.archived).map((p) => p.id)}
        canDelete={isSuper} onDone={changed} testid="financial-paychecks-bulk" />}
      {!rows ? <Empty>Loading…</Empty> : list.length ? (
        <ul>
          {list.map((p) => (
            <div key={p.id} data-testid={`financial-paycheck-${p.id}`} className="flex items-start gap-2">
              {can && <input type="checkbox" className="mt-4" checked={sel.includes(p.id)} onChange={() => setSel((s) => (s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id]))}
                data-testid={`financial-paycheck-check-${p.id}`} aria-label="Select paycheck" />}
              <div className="flex-1 min-w-0">
                {p.archived && <span data-testid={`financial-paycheck-archived-${p.id}`} className="inline-block mt-2 px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-300 font-mono text-[8px] tracking-[0.18em] uppercase">Archived · not in balances</span>}
                <PaycheckRow pc={p} inCampus onChanged={changed} />
              </div>
            </div>
          ))}
        </ul>
      ) : <Empty>{view === "archived" ? "No archived paychecks." : "No paychecks."}</Empty>}
    </Section>
  );
}

/** Program fee breakdown ($31.25 slices of case management per verified homework) — kept apart from the money ledger. */
function FeeRows({ fees }) {
  const rows = fees?.items || [];
  return (
    <Section title={`Program fees · ${money(rows.filter((t) => !t.excluded_from_balance).reduce((a, t) => a + (t.direction === "credit" ? t.amount : -t.amount), 0))} attributed`} testId="financial-program-fees">
      {rows.length ? (
        <Collapsible label={rows.length < (fees.total || 0) ? `Latest ${rows.length} of ${fees.total} entries` : `${rows.length} ${rows.length === 1 ? "entry" : "entries"}`} testId="financial-program-fees">
          {rows.map((t) => <Line key={t.id} testId={`financial-fee-row-${t.id}`} right={`${t.direction === "debit" ? "−" : "+"}${money(t.amount)}`}
            main="Program fee" sub={`${fmt(t.created_at)}${t.description ? ` · ${t.description}` : ""}${t.excluded_from_balance ? " · not counted" : ""}`} />)}
        </Collapsible>
      ) : <Empty>No program fee entries.</Empty>}
    </Section>
  );
}

export default function FinancialTab({ resident, focus }) {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [src, setSrc] = useState("all");
  const [lf, setLf] = useState(EMPTY_LEDGER_FILTERS);
  const sa = useIsSuper();
  const { user } = useAuth();
  const canMove = sa || (user?.staff_permissions || []).includes("move_money");
  const canDefer = sa || (user?.staff_permissions || []).includes("defer_dues");
  const inSession = !!useSessionCheckin();
  const [open, setOpen] = useState(focus?.undoWeek ? "weeks" : null);
  useEffect(() => { if (focus?.undoWeek) setOpen("weeks"); }, [focus?.undoWeek, focus?.undoPaycheckId]);
  const [views, setViews] = useState({ ledger: "active", savings: "active", expense: "active" });
  const setView = (k) => (v) => setViews((x) => ({ ...x, [k]: v }));
  const bump = () => setTick((n) => n + 1);
  const [picked, setPicked] = useState({ ledger: [], savings: [], expense: [] });
  const sel = { ...picked, toggle: (k) => (id) => setPicked((p) => ({ ...p, [k]: p[k].includes(id) ? p[k].filter((x) => x !== id) : [...p[k], id] })) };
  const bulk = (k, rows) => sa && <BulkBar kind={k} rows={rows} sel={picked[k]} setSel={(v) => setPicked((p) => ({ ...p, [k]: v }))} onChanged={bump} />;
  useEffect(() => setPicked({ ledger: [], savings: [], expense: [] }), [views, tick]);
  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    window.addEventListener("fin:money-changed", on);
    return () => window.removeEventListener("fin:money-changed", on);
  }, []);
  useEffect(() => {
    const rid = resident.id;
    Promise.all([
      finApi.get(`/residents/${rid}/balances`).catch(() => null),
      finApi.get(`/residents/${rid}/ledger`, { page_size: 200, view: views.ledger, scope: inSession ? "session" : "ledger", source: src, ...Object.fromEntries(Object.entries(lf).filter(([, v]) => v)) }).catch(() => null),
      finApi.get("/paychecks", { resident_id: rid }).catch(() => ({ paychecks: [] })),
      finApi.get("/savings/requests", { resident_id: rid, view: views.savings }).catch(() => ({ requests: [] })),
      finApi.get(`/residents/${rid}/legacy`).catch(() => ({})),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/lp/residents/${rid}/financial/expenses`,
        { params: { view: views.expense }, headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } }).then((r) => r.data).catch(() => ({ expenses: [] })),
      inSession ? Promise.resolve(null) : finApi.get(`/residents/${rid}/ledger`, { page_size: 200, scope: "fees" }).catch(() => null),
      canDefer ? finApi.get(`/flow/dues/${rid}`).catch(() => null) : Promise.resolve(null),
    ]).then(([b, l, p, s, g, x, f, du]) => setD({ balances: b?.balances || {}, locTotal: b?.line_of_credit_total, ledger: l, paychecks: p.paychecks, requests: s.requests, legacy: g, expenses: x, fees: f, dues: du }));
  }, [resident.id, tick, views, inSession, canDefer, src, lf]);
  if (!d) return <p data-testid="financial-loading" className="text-sm text-nex-muted">Loading…</p>;
  const rows = d.ledger?.items || d.ledger?.rows || d.ledger?.transactions || [];
  const payouts = d.legacy?.session_payouts || [];
  const staffish = user?.role !== "partner_user" && user?.role !== "resident";
  const pcs = d.paychecks || [];
  const waiting = pcs.filter((p) => ["pending_verification", "flagged"].includes(p.status)).length;
  const lastPc = [...pcs].sort((a, b) => String(b.pay_period_end).localeCompare(String(a.pay_period_end)))[0];
  const feeTotal = (d.fees?.items || []).filter((t) => !t.excluded_from_balance).reduce((a, t) => a + (t.direction === "credit" ? t.amount : -t.amount), 0);
  const changedPc = () => { bump(); window.dispatchEvent(new CustomEvent("fin:paycheck-logged")); };
  const close = () => setOpen(null);
  // [id, title, Icon, tone, short line, live stat, live note, alert, content]
  const CARDS = [
    canDefer && ["weeks", "Every week of the stay", CalendarRange, "financial", "Rent & case management week by week, Line of Credit",
      d.dues ? money(d.dues.line_of_credit) : "—", d.dues ? `${d.dues.rent_weeks_behind}R / ${d.dues.cm_weeks_behind}CM wks behind` : "", d.dues && d.dues.line_of_credit > 0,
      <DuesResident rid={resident.id} quiet focus={focus} />],
    staffish && ["log", "Log paycheck", Banknote, "ops", "Enter a paycheck; it fills the payment order for you",
      lastPc ? lastPc.pay_period_end : "None yet", lastPc ? "last pay period" : "", false,
      <StaffLogPaycheck residentId={resident.id} label="Log paycheck" where="from the Financial board" startOpen onCancel={close} onDone={() => { changedPc(); close(); }} />],
    staffish && canMove && ["move", "Move money", ArrowLeftRight, "data", "Between buckets, Line of Credit, cash out, resident advances, bills",
      money(d.balances.checking || 0), "cash in hand", false,
      <MoveMoney residentId={resident.id} balances={d.balances} startOpen onCancel={close} onDone={() => { bump(); close(); }} />],
    ["paychecks", "Paychecks", Receipt, "financial", "Every paycheck: edit, correct, void or archive",
      String(pcs.length), waiting ? `${waiting} waiting for review` : "all reviewed", waiting > 0,
      <PaycheckList key={`pl-${tick}`} resident={resident} hideId={focus?.paycheckId} onMoney={() => setTick((n) => n + 1)} />],
    ["savings", "Savings requests", PiggyBank, "people", "Withdrawal requests from savings",
      String(d.requests.length), `${d.requests.filter((r) => r.status === "pending").length} pending`, d.requests.some((r) => r.status === "pending"),
      <Section title={`Savings requests · ${d.requests.length}`} testId="financial-savings-requests">
        {sa && <ArchiveViewChips prefix="financial-savings" view={views.savings} setView={setView("savings")} size="sm" />}
        {bulk("savings", d.requests)}
        {d.requests.length ? d.requests.map((r) => (
          <Tools key={r.id} sel={sel} on={sa} kind="savings" row={r} onChanged={bump}><Line testId={`financial-savings-request-${r.id}`} right={money(r.amount)}
            main={`${words(r.request_type)} · ${r.status}`} sub={`${r.reason} · requested ${fmt(r.requested_at)} · reviewed ${fmt(r.reviewed_at)} by ${r.reviewed_by?.name || "—"}`} /></Tools>
        )) : <Empty>No savings requests.</Empty>}
      </Section>],
    ["expenses", "PACT Life Pass expenses", ShoppingBag, "comms", "What the resident logged spending in PACT Life Pass",
      money(d.expenses.this_month_total || 0), `this month · ${d.expenses.expenses.length}`, false,
      <Section title={`Expenses logged in PACT Life Pass · ${money(d.expenses.this_month_total || 0)} this month`} testId="financial-app-expenses">
        {sa && <ArchiveViewChips prefix="financial-expenses" view={views.expense} setView={setView("expense")} size="sm" />}
        {bulk("expense", d.expenses.expenses)}
        {d.expenses.expenses.length ? d.expenses.expenses.map((x) => (
          <Tools key={x.id} sel={sel} on={sa} kind="expense" row={x} onChanged={bump}><Line testId={`financial-app-expense-${x.id}`} right={money(x.amount)} main={`${words(x.category)} · ${fmt(`${x.spent_on}T12:00:00`)}`} sub={x.note || "—"} /></Tools>
        )) : <Empty>No expenses logged in the app.</Empty>}
      </Section>],
    ["ledger", "Ledger", BookText, "system", inSession ? "Paychecks, savings, line of credit and penalties" : "Every money line, with fix / void / move tools",
      String(d.ledger?.source_counts ? d.ledger.source_counts.old + d.ledger.source_counts.new : (d.ledger?.total ?? rows.length)), "entries", false,
      <Section title={inSession ? "Ledger · paychecks, savings, line of credit and penalties" : "Ledger"} testId="financial-ledger">
        {sa && <ArchiveViewChips prefix="financial-ledger" view={views.ledger} setView={setView("ledger")} size="sm" />}
        <SourceChips src={src} setSrc={setSrc} counts={d.ledger?.source_counts} />
        <LedgerFilters value={lf} onChange={setLf} />
        {bulk("ledger", rows)}
        {rows.length ? rows.map((t) => (
          <Tools key={t.id} sel={sel} on={sa} kind="ledger" row={t} onChanged={bump}><Line testId={`financial-ledger-row-${t.id}`} right={`${t.direction === "debit" ? "−" : "+"}${money(t.amount)}`} tag={<OriginTag id={t.id} origin={t.origin} />}
            main={`${words(t.bucket)} · ${words(t.type)}`} sub={`${fmt(t.created_at)} · balance after ${money(t.balance_after ?? 0)}${t.description ? ` · ${t.description}` : ""}`} /></Tools>
        )) : <Empty>{lf.groups || lf.date_from || lf.date_to ? "No ledger entries match these filters." : "No ledger entries."}</Empty>}
        {rows.length < (d.ledger?.total || 0) && <p className="text-[11px] text-nex-muted">Showing the latest {rows.length} of {d.ledger.total}.</p>}
      </Section>],
    !inSession && ["fees", "Program fees", Landmark, "content", "PACT Fee slices per verified homework",
      money(feeTotal), "attributed", false, <FeeRows fees={d.fees} />],
    ["payouts", "Old platform payouts", History, "system", "Staff session payouts — history only, not payable",
      String(payouts.length), money(payouts.reduce((a, p) => a + (p.amount || 0), 0)), false,
      <Section title="Staff session payouts · old platform history" testId="financial-legacy-payouts">
        {payouts.length ? payouts.map((p) => (
          <Line key={p.id} testId={`financial-legacy-payout-${p.id}`} right={money(p.amount)}
            main={`${words(p.source)} · ${p.case_manager_name || "—"}`} sub={`${fmt(p.session_date)} · ${p.week_key || ""} · was ${p.legacy_status}${p.amount_resident ? ` · resident share ${money(p.amount_resident)}` : ""}`} />
        )) : <Empty>None.</Empty>}
      </Section>],
  ].filter(Boolean);
  const active = CARDS.find((c) => c[0] === open);
  return (
    <div data-testid="financial-tab" className="space-y-6">
      {staffish && <FinancialReviewToggle rid={resident.id} />}
      {sa && <p className="text-[11px] text-nex-muted">Super admins can edit, archive or permanently delete any money line here. Archiving takes it out of balances; unarchiving puts it back.</p>}
      <div data-testid="financial-balances" className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4">
        {BUCKETS.map(([k, l]) => <BalanceTile key={k} k={k} label={l} v={k === "loan" && d.locTotal != null ? -d.locTotal : d.balances[k]} />)}
      </div>
      {focus?.paycheckId && d.paychecks.some((p) => p.id === focus.paycheckId) && (
        <Section title="Editing paycheck" testId="financial-focus-paycheck">
          <ul className="border border-nex-gold/40 rounded-sm"><PaycheckRow pc={d.paychecks.find((p) => p.id === focus.paycheckId)} inCampus startEditing onChanged={changedPc} /></ul>
        </Section>
      )}
      <ComingUp residentId={resident.id} tick={tick} />
      <ReconcilePanel key={`rc-${tick}`} residentId={resident.id} onChanged={bump} />
      <OldPortalToggle onChange={bump} />
      {active ? (
        <div data-testid={`financial-card-open-${active[0]}`} className="space-y-4">
          <button type="button" onClick={close} data-testid="financial-cards-back"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold hover:text-nex-goldbright transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> All finance cards · <span className="text-nex-silver">{active[1]}</span></button>
          {active[8]}
        </div>
      ) : (
        <div data-testid="financial-cards">
          <ArrangeBoard board="resident_financial" label="finance cards" ids={CARDS.map((c) => c[0])} labels={Object.fromEntries(CARDS.map((c) => [c[0], c[1]]))} renderTile={(cid) => {
            const [id, label, Icon, tone, desc, stat, note, alert] = CARDS.find((c) => c[0] === cid);
            return <CardTile testid={`financial-card-${id}`} label={label} desc={desc} Icon={Icon} tone={tone} stat={stat} note={note} alert={alert} onOpen={() => setOpen(id)} />;
          }} />
        </div>
      )}
    </div>
  );
}
